import {
  LayoutDashboard,
  Users,
  Building2,
  Shield,
  FileText,
  Mail,
  Settings,
  Activity,
  Server,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Upload,
  DollarSign,
  Receipt,
  Tag,
  Gauge,
  CreditCard,
  Wallet,
  ArrowRightLeft,
  Search,
  UserCheck,
  Eye,
  Gavel,
  BarChart3,
  BookOpen,
  Wrench,
  Bug,
  Webhook,
  TestTube,
  ClipboardCheck,
  TrendingUp,
  Landmark,
  Scale,
  FolderOpen,
  Database,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Banknote,
  CircleDollarSign,
  Truck,
  LineChart,
  Clock,
  Target,
  Layers,
  GitBranch,
  ToggleRight,
  Code,
  Lock,
  FolderCheck,
  Boxes,
  KeyRound,
  Factory,
  Globe,
  List,
  Radar,
  Paintbrush,
  Grid3X3,
  Hash,
  Sprout,
  Bell,
  MessageSquare,
  ArrowDownToLine,
  Ban,
  SlidersHorizontal,
  Ticket,
  Euro,
  RotateCcw,
  Send,
  Package,
  Palette,
  Archive,
  Gift,
  Crown,
  ArrowUpDown,
  CheckCircle2,
  History,
  FlaskConical,
  Brain,
  Inbox,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: string;
}

export interface NavSubGroup {
  title: string;
  items: NavItem[];
}

export interface NavSection {
  title: string;
  icon: LucideIcon;
  color: string; // hex color for dot indicator
  sectionPermission?: string;
  items: NavItem[];
  /** Optional: when present, items are organized into collapsible sub-groups under this section. */
  subGroups?: NavSubGroup[];
}

/** Groups shown in the main scrollable area */
export const adminNavSections: NavSection[] = [

  // ─── PAYNOVUS OPERATIONS (super_admin/treasury only) ────
  {
    title: 'PayNovus Operations',
    icon: Landmark,
    color: '#d97706',
    sectionPermission: 'treasury.view',
    items: [
      { title: 'Treasury & House Accounts', url: '/admin/operations/treasury', icon: Landmark },
      { title: 'Execute Payment', url: '/admin/operations/payments', icon: Send },
      { title: 'Operating Statement', url: '/admin/operations/statement', icon: FileText },
      { title: 'Team Management', url: '/admin/operations/team', icon: Users },
    ],
  },

  // ─── CORE OPERATIONS ────────────────────────────────────
  {
    title: 'Dashboard & Applications',
    icon: FileText,
    color: '#0ea5e9',
    items: [
      { title: 'Dashboard', url: '/admin', icon: LayoutDashboard, end: true },
      { title: 'Individual Applications', url: '/admin/applications/individual', icon: Users },
      { title: 'Corporate Applications', url: '/admin/applications/corporate', icon: Building2 },
    ],
  },
  {
    title: 'Client Management',
    icon: Users,
    color: '#8b5cf6',
    items: [
      { title: 'All Clients', url: '/admin/clients', icon: Users, end: true },
      { title: 'Client Lifecycle', url: '/admin/client-lifecycle', icon: GitBranch },
      { title: 'Pricing & Billing', url: '/admin/pricing', icon: Tag },
      { title: 'Transaction Limits', url: '/admin/pricing/limits', icon: Gauge },
      { title: 'Referrers / Introducers', url: '/admin/referrers', icon: Users },
    ],
  },
  {
    title: 'Payments & Transfers',
    icon: ArrowRightLeft,
    color: '#10b981',
    items: [
      { title: 'Payments Dashboard', url: '/admin/payments/sepa-dashboard', icon: LayoutDashboard },
      { title: 'Outbound Payments', url: '/admin/payments/outbound', icon: Banknote },
      { title: 'Inbound Payments', url: '/admin/payments/inbound', icon: ArrowDownToLine },
      { title: 'Inbound Suspense', url: '/admin/payments/inbound-suspense', icon: AlertTriangle },
      { title: 'Stranded Intents', url: '/admin/payments/stranded', icon: AlertTriangle },
      { title: 'Release Rejected', url: '/admin/payments/release-rejected', icon: AlertTriangle },
      { title: 'Payment Investigations', url: '/admin/payments/investigations', icon: Search },
      { title: 'SEPA Recalls (EUR)', url: '/admin/payments/recalls', icon: RotateCcw },
      { title: 'SEPA Participants (EUR)', url: '/admin/payments/participants', icon: Building2 },
      { title: 'SEPA Webhooks (EUR)', url: '/admin/payments/sepa-webhooks', icon: Webhook },
      { title: 'Rail Config', url: '/admin/payments/rail-config', icon: Settings },
      { title: 'Rail Health', url: '/admin/payments/rail-health', icon: Activity },
      { title: 'Orchestration Engine', url: '/admin/payments/orchestration', icon: Settings },
      { title: 'CC Accounts', url: '/admin/payments/cc-accounts', icon: Landmark },
    ],
  },
  {
    title: 'Treasury & FX Desk',
    icon: TrendingUp,
    color: '#f59e0b',
    items: [
      { title: 'Treasury Dashboard', url: '/admin/treasury', icon: LayoutDashboard, end: true },
      { title: 'FX Pricing & Spreads', url: '/admin/treasury/pricing', icon: LineChart },
      { title: 'Currency Pair Matrix', url: '/admin/treasury/pair-matrix', icon: Grid3X3 },
      { title: 'Execution Desk', url: '/admin/treasury/execution-desk', icon: BarChart3 },
      { title: 'Autopilot Control', url: '/admin/treasury/autopilot', icon: Target },
      { title: 'Forward Book', url: '/admin/treasury/forward-book', icon: BookOpen },
      { title: 'Settlements', url: '/admin/treasury/settlements', icon: Landmark },
      { title: 'EOD Checklist', url: '/admin/treasury/eod', icon: ClipboardCheck },
      { title: 'Closing Providers', url: '/admin/treasury/closing-providers', icon: Building2 },
    ],
  },

  // ─── CARD ISSUING ───────────────────────────────────────
  {
    title: 'Card Issuing',
    icon: CreditCard,
    color: '#7c3aed',
    items: [],
    subGroups: [
      {
        title: 'Card Management',
        items: [
          { title: 'Cardholders', url: '/admin/cards/cardholders', icon: Users },
          { title: 'Cards', url: '/admin/cards/all-cards', icon: CreditCard },
          { title: 'Transactions', url: '/admin/cards/transactions', icon: ArrowRightLeft },
        ],
      },
      {
        title: 'Products',
        items: [
          { title: 'Card Products', url: '/admin/cards/products', icon: Package },
          { title: 'Design Library', url: '/admin/cards/designs', icon: Palette },
          { title: 'Packages/Carriers', url: '/admin/cards/packages', icon: Boxes },
          { title: 'Inventory Management', url: '/admin/cards/inventory', icon: Archive },
        ],
      },
      {
        title: 'Fee Schedule',
        items: [
          { title: 'Fee Tables', url: '/admin/cards/fee-tables', icon: DollarSign },
          { title: 'Limits', url: '/admin/cards/limits', icon: Gauge },
          { title: 'Promotions', url: '/admin/cards/promotions', icon: Gift },
          { title: 'Plans', url: '/admin/cards/plans', icon: Crown },
        ],
      },
      {
        title: 'Cardholder Requests',
        items: [
          { title: 'Inquiries/Complaints', url: '/admin/cards/inquiries', icon: MessageSquare },
          { title: 'Limit Requests', url: '/admin/cards/limit-requests', icon: ArrowUpDown },
          { title: 'TX Confirmations', url: '/admin/cards/tx-confirmations', icon: CheckCircle },
          { title: 'Card Orders', url: '/admin/cards/orders', icon: Package },
        ],
      },
      {
        title: 'Dispute Management',
        items: [
          { title: 'New Disputes', url: '/admin/cards/disputes/new', icon: AlertTriangle },
          { title: 'In Progress', url: '/admin/cards/disputes/in-progress', icon: Clock },
          { title: 'Closed Disputes', url: '/admin/cards/disputes/closed', icon: CheckCircle2 },
          { title: 'All Disputes', url: '/admin/cards/disputes/all', icon: FileText },
        ],
      },
      {
        title: 'Reporting',
        items: [
          { title: 'Full Card List', url: '/admin/cards/reports?type=card_list', icon: List },
          { title: 'Transactions Report', url: '/admin/cards/reports?type=transactions', icon: ArrowRightLeft },
          { title: 'Load/Reload', url: '/admin/cards/reports?type=load_reload', icon: RotateCcw },
          { title: 'Active/Inactive', url: '/admin/cards/reports?type=card_status', icon: BarChart3 },
          { title: 'PM Balance', url: '/admin/cards/reports?type=pm_balance', icon: Building2 },
          { title: 'Tx Processing Rev', url: '/admin/cards/reports?type=tx_revenue', icon: TrendingUp },
          { title: 'BNB Reporting', url: '/admin/cards/reports?type=bnb', icon: Landmark },
          { title: 'Visa/Mastercard', url: '/admin/cards/reports?type=scheme', icon: Shield },
        ],
      },
    ],
  },
  {
    title: 'Card Operations',
    icon: Wrench,
    color: '#6d28d9',
    items: [
      { title: 'MCC Lists', url: '/admin/cards/mcc-lists', icon: List },
      { title: 'Merchant Risk Rules', url: '/admin/cards/merchant-risk', icon: ShieldAlert },
      { title: 'Card Sandbox', url: '/admin/cards/sandbox', icon: FlaskConical },
      { title: 'Activity History', url: '/admin/cards/activity-history', icon: History },
      { title: 'Card Limit Rules', url: '/admin/cards/limit-rules', icon: Gauge },
      { title: 'Rule Runs', url: '/admin/cards/rule-runs', icon: Activity },
      { title: 'Settlement Recon', url: '/admin/cards/reconciliation', icon: ClipboardCheck },
      { title: 'Dispute Recon', url: '/admin/cards/disputes/reconciliation', icon: ClipboardCheck },
    ],
  },
  {
    title: 'BaaS Partners',
    icon: Banknote,
    color: '#ec4899',
    items: [
      { title: 'BaaS Hub', url: '/admin/baas', icon: LayoutDashboard, end: true },
      { title: 'Partner Registry', url: '/admin/integrations/baas/partners', icon: Building2 },
      { title: 'BaaS Tickets', url: '/admin/baas/tickets', icon: Ticket },
      { title: 'Client Support', url: '/admin/support-tickets', icon: MessageSquare },
      { title: 'Control Requests', url: '/admin/baas/requests', icon: FileText },
    ],
  },
  {
    title: 'Program Management',
    icon: Crown,
    color: '#d946ef',
    items: [
      { title: 'Program Managers', url: '/admin/card-program/managers', icon: Building2 },
      { title: 'Card Programs', url: '/admin/card-program/programs', icon: CreditCard },
      { title: 'Program Cards', url: '/admin/card-program/cards', icon: CreditCard },
      { title: 'Program Transactions', url: '/admin/card-program/transactions', icon: ArrowRightLeft },
      { title: 'Program Disputes', url: '/admin/card-program/disputes', icon: Gavel },
      { title: 'Controls & Limits', url: '/admin/card-program/controls', icon: Shield },
      { title: 'Settlement & Recon', url: '/admin/card-program/settlement', icon: Scale },
      { title: 'Program Compliance', url: '/admin/card-program/compliance/overview', icon: ShieldAlert },
      { title: 'API & Branding', url: '/admin/card-program/api', icon: KeyRound },
    ],
  },

  // ─── FINANCE ────────────────────────────────────────────
  {
    title: 'Ledger & Finance',
    icon: Landmark,
    color: '#14b8a6',
    items: [
      { title: 'Ledger Overview', url: '/admin/ledger', icon: LayoutDashboard },
      { title: 'Chart of Accounts', url: '/admin/ledger/accounts', icon: FolderOpen },
      { title: 'Journal & Transactions', url: '/admin/ledger/journal', icon: FileText },
      { title: 'Balances', url: '/admin/ledger/balances', icon: Wallet },
      { title: 'Trial Balance & CoA Coverage', url: '/admin/ledger/trial-balance', icon: Scale },
      { title: 'БНБ Statutory P&L', url: '/admin/ledger/bnb-pnl', icon: FileText },
      { title: 'Revenue Analytics', url: '/admin/ledger/revenue', icon: TrendingUp },
      { title: 'Period Close', url: '/admin/ledger/period-close', icon: Clock },
      { title: 'Financial Reports', url: '/admin/ledger/financial-reports', icon: BarChart3 },
      { title: 'IBAN Registry', url: '/admin/ledger/iban-registry', icon: Hash },
      { title: 'Reconciliation', url: '/admin/ledger/reconciliation', icon: Scale },
      { title: 'Inbound Return Queue (4995)', url: '/admin/ledger/inbound-return-queue', icon: Inbox },
      { title: 'Returns 4-Eyes Approval (4996)', url: '/admin/ledger/returns-approval-queue', icon: ShieldCheck },
      { title: 'Integrity Audit', url: '/admin/ledger/integrity-audit', icon: Shield },
      { title: 'GL Account Mapping', url: '/admin/ledger/gl-mapping', icon: Shield },
      { title: 'Safeguarding', url: '/admin/ledger/safeguarding', icon: ShieldCheck },
      { title: 'Bookkeeping', url: '/admin/bookkeeping', icon: BookOpen },
      { title: 'Invoices', url: '/admin/bookkeeping/invoices', icon: FileText },
      { title: 'EU VAT Rates', url: '/admin/bookkeeping/vat-rates', icon: Receipt },
    ],
  },

  // ─── COMPLIANCE OPERATIONS (daily queues) ────────────────
  {
    title: 'Compliance Operations',
    icon: ShieldAlert,
    color: '#f97316',
    items: [
      { title: 'Command Center', url: '/admin/compliance/command-center', icon: Radar },
      { title: 'Screening Queue', url: '/admin/compliance/screening', icon: Search },
      { title: 'Monitoring Alerts', url: '/admin/compliance/alerts', icon: AlertTriangle },
      { title: 'Cases', url: '/admin/compliance/cases', icon: Shield },
      { title: 'Held Intents', url: '/admin/compliance/held-intents', icon: ShieldAlert },
      { title: 'Interdictions', url: '/admin/compliance/tm/interdictions', icon: Ban },
      { title: 'EDD Requests', url: '/admin/compliance/edd', icon: FileText },
      { title: 'MLRO Queue', url: '/admin/compliance/mlro', icon: Scale },
      { title: 'Approval Queue', url: '/admin/approvals', icon: CheckCircle },
    ],
  },
  // ─── REPORTING & ENFORCEMENT ─────────────────────────────
  {
    title: 'Reporting & Controls',
    icon: FileText,
    color: '#fb923c',
    items: [
      { title: 'SAR Workspace', url: '/admin/compliance/sar', icon: FileText },
      { title: 'BNB Declarations', url: '/admin/compliance/bnb-declarations', icon: FileText },
      { title: 'BNB Reports', url: '/admin/compliance/bnb-reports', icon: Building2 },
      { title: 'Garnishment Register', url: '/admin/compliance/garnishments', icon: Scale },
      { title: 'Client Controls', url: '/admin/compliance/client-controls', icon: ShieldOff },
      { title: 'Account Controls', url: '/admin/compliance/account-controls', icon: Lock },
      { title: 'IP Blocklist', url: '/admin/compliance/ip-blocklist', icon: ShieldAlert },
    ],
  },
  // ─── REGISTRIES & RULE ENGINE ────────────────────────────
  {
    title: 'Risk & TM Engine',
    icon: Shield,
    color: '#ef4444',
    items: [
      { title: 'Transaction Monitoring', url: '/admin/compliance/transaction-monitoring', icon: Activity },
      { title: 'TM Rules Library', url: '/admin/compliance/tm/rules', icon: ToggleRight },
      { title: 'TM Parameters', url: '/admin/compliance/tm/parameters', icon: SlidersHorizontal },
      { title: 'TM Coverage', url: '/admin/compliance/tm/coverage-dashboard', icon: BarChart3 },
      { title: 'Risk Matrix', url: '/admin/risk-matrix', icon: Shield },
      { title: 'KYB Risk Matrix', url: '/admin/compliance/risk-matrix-versions', icon: Layers },
      { title: 'High Risk Industries', url: '/admin/compliance/industry-risk', icon: Factory },
      { title: 'Data Extractors', url: '/admin/compliance/extractors', icon: Database },
      { title: 'Extraction Results', url: '/admin/compliance/extraction-results', icon: BarChart3 },
      { title: 'Bank Registry', url: '/admin/compliance/banks', icon: Landmark },
      { title: 'Counterparty Profiles', url: '/admin/compliance/counterparties', icon: Users },
      { title: 'Whitelist Registry', url: '/admin/compliance/whitelists', icon: FolderCheck },
      { title: 'Payment Whitelists', url: '/admin/compliance/payment-whitelists', icon: ShieldCheck },
      { title: 'MI Reports', url: '/admin/compliance/mi/overview', icon: BarChart3 },
      { title: 'Compliance Settings', url: '/admin/compliance/settings', icon: Settings },
    ],
  },
  {
    title: 'Enterprise Risk',
    icon: Shield,
    color: '#b91c1c',
    items: [
      { title: 'ERM Dashboard', url: '/admin/erm', icon: Shield },
      { title: 'KRI Monitoring', url: '/admin/kri', icon: Activity },
      { title: 'Incidents', url: '/admin/incidents', icon: AlertCircle },
      { title: 'Board Reports', url: '/admin/board-reports', icon: BarChart3 },
    ],
  },

  // ─── PLATFORM ───────────────────────────────────────────
  {
    title: 'Open Banking',
    icon: Globe,
    color: '#3b82f6',
    items: [
      { title: 'TPP Registry', url: '/admin/openbanking/tpps', icon: Building2 },
      { title: 'AISP Consents', url: '/admin/openbanking/consents', icon: Shield },
      { title: 'Payment Consents', url: '/admin/openbanking/payment-consents', icon: Send },
      { title: 'Access Log', url: '/admin/openbanking/access-log', icon: FileText },
      { title: 'OB Settings', url: '/admin/openbanking/settings', icon: Settings },
    ],
  },
  {
    title: 'System Monitoring',
    icon: Activity,
    color: '#22d3ee',
    items: [
      { title: 'Audit Log', url: '/admin/monitoring/audit-log', icon: Shield },
      { title: 'Error Log', url: '/admin/monitoring/error-log', icon: AlertTriangle },
      { title: 'AI Anomaly Detection', url: '/admin/monitoring/anomaly-detection', icon: Brain },
    ],
  },
  {
    title: 'Security & Access',
    icon: ShieldCheck,
    color: '#6366f1',
    items: [
      { title: 'Users & Roles', url: '/admin/security/users', icon: Users },
      { title: 'Security Audit', url: '/admin/security/audit', icon: FileText },
      { title: 'SCA Events', url: '/admin/security/sca-events', icon: Shield },
      { title: 'Login Lockout', url: '/admin/security/lockout', icon: Lock },
      { title: 'IP Allowlist', url: '/admin/security/ip-allowlist', icon: Shield },
      { title: 'Onboarding Reminders', url: '/admin/settings/onboarding-reminders', icon: Bell },
    ],
  },
  {
    title: 'Integrations',
    icon: Webhook,
    color: '#a855f7',
    items: [
      { title: 'Integrations Registry', url: '/admin/integrations', icon: Boxes, end: true },
      { title: 'HubSpot CRM', url: '/admin/integrations/hubspot', icon: Users },
      { title: 'Accounting Feeds', url: '/admin/integrations/accounting', icon: BookOpen },
      { title: 'Webhook Monitor', url: '/admin/integrations/webhooks', icon: Webhook },
      { title: 'CC Webhooks', url: '/admin/integrations/currencycloud/webhooks', icon: Webhook },
      { title: 'CC Capabilities', url: '/admin/integrations/currencycloud/capabilities', icon: TrendingUp },
      { title: 'Currency Registry', url: '/admin/integrations/currencies', icon: Banknote },
    ],
  },
  {
    title: 'ISO 27001',
    icon: FolderCheck,
    color: '#2563eb',
    items: [
      { title: 'ISO Hub', url: '/admin/iso27001', icon: Shield, end: true },
      { title: 'SoA', url: '/admin/iso/soa', icon: ClipboardCheck },
      { title: 'Evidence Pack', url: '/admin/iso/evidence', icon: FolderOpen },
      { title: 'Control Testing', url: '/admin/iso/testing', icon: TestTube },
    ],
  },
  {
    title: 'Ops & Governance',
    icon: Wrench,
    color: '#64748b',
    items: [
      { title: 'Ops Overview', url: '/admin/ops', icon: Wrench, end: true },
      { title: 'Incidents', url: '/admin/ops/incidents', icon: AlertCircle },
      { title: 'Ops Runbook', url: '/admin/ops/runbook', icon: BookOpen },
      { title: 'Policies & Training', url: '/admin/policies', icon: BookOpen },
      { title: 'Activity Log', url: '/admin/activity', icon: Activity },
    ],
  },
  {
    title: 'Diagnostics & Tools',
    icon: TestTube,
    color: '#71717a',
    items: [
      // ── E2E Suites ──
      { title: 'Core E2E', url: '/admin/diagnostics/core-e2e', icon: TestTube },
      { title: 'Individual Onboarding E2E', url: '/admin/diagnostics/onboarding-individual-e2e', icon: TestTube },
      { title: 'Corporate Onboarding E2E', url: '/admin/diagnostics/onboarding-corporate-e2e', icon: Building2 },
      { title: 'Client Mgmt E2E', url: '/admin/diagnostics/admin-client-mgmt-e2e', icon: TestTube },
      { title: 'Payments Full E2E', url: '/admin/diagnostics/payments-full-e2e', icon: TestTube },
      { title: 'FX Full E2E', url: '/admin/diagnostics/fx-full-e2e', icon: TestTube },
      { title: 'Cards Full E2E v2', url: '/admin/diagnostics/cards-full-e2e-v2', icon: CreditCard },
      { title: 'Compliance Full E2E', url: '/admin/diagnostics/compliance-full-e2e', icon: Shield },
      { title: 'CC E2E Suite', url: '/admin/diagnostics/currencycloud-e2e-suite', icon: TestTube },
      { title: 'Financial E2E', url: '/admin/diagnostics/financial-integrity-e2e', icon: TestTube },
      { title: 'BaaS E2E', url: '/admin/diagnostics/baas-e2e', icon: TestTube },
      { title: 'PM Smoke Test', url: '/admin/diagnostics/pm-smoke-test', icon: Building2 },
      { title: 'Support & Ticketing E2E', url: '/admin/diagnostics/support-ticketing-e2e', icon: TestTube },
      // ── Tools ──
      { title: 'Comprehensive Seed', url: '/admin/diagnostics/comprehensive-seed', icon: Database },
      { title: 'Sync Health', url: '/admin/sync-health', icon: Server },
      { title: 'Feature Flags', url: '/admin/system/feature-flags', icon: ToggleRight },
      { title: 'Flow Diagrams', url: '/admin/flows', icon: GitBranch },
    ],
  },
];

/** Section dividers — insert visual breaks between logical groups. */
export const navDividerAfter = new Set([
  'PayNovus Operations',
  'Program Management',
  'Enterprise Risk',
  'ISO 27001',
]);

/** Pinned utility items shown in the sidebar footer */
export const adminStandaloneItems: NavItem[] = [
  { title: 'Tasks', url: '/admin/tasks', icon: ClipboardCheck },
  { title: 'Inbox', url: '/admin/inbox', icon: Bell },
  { title: 'Performance', url: '/admin/performance', icon: Activity },
  { title: 'Sessions', url: '/admin/sessions', icon: Clock },
  { title: 'Settings', url: '/admin/settings', icon: Settings },
];

/** Group labels for the dividers */
export const navGroupLabels: Record<string, string> = {
  'Dashboard & Applications': 'Core Operations',
  'Card Issuing': 'Card Operations',
  'Ledger & Finance': 'Finance & Compliance',
  'Open Banking': 'Platform & Admin',
  'Diagnostics & Tools': 'Developer Tools',
};
